var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useCallback, useEffect, useState } from 'react';
import { FontAwesomeIconWrapper } from '@icon/fontawesome/FontAwesomeIconWrapper';
import classNames from 'classnames';
import Checkbox from '@widget/forms/inputs/Checkbox';
import TableCell from './TableCell';
import { LoadingSpinner } from '@widget/loaders';
import Input from '@widget/forms/inputs/Input';
import { SkeletonLoader } from '@widget/loaders';
import ButtonComponent from '@widget/buttons/ButtonComponent';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import useRowGestures from '@hooks/useRowGestures';
/**
 * @export
 * Skeleton loading row component displayed while table data is loading.
 * Shows placeholder skeleton loaders in each cell.
 *
 * @example
 * ```tsx
 * <SkeletonTableRow columns={processedColumns} />
 * ```
 */
const SkeletonTableRow = (props) => {
    let newColumns = Object.entries(props.columns).map(([k, column]) => {
        return [k, Object.assign(Object.assign({}, column), { renderColumn: () => _jsx(SkeletonLoader, { width: '9rem' }) })];
    });
    return _jsx(TableRow, { columns: Object.fromEntries(newColumns), row: {}, isRowSelected: (r) => false });
};
/**
 * @export
 * Individual table row component that renders a single row with cells.
 * Supports regular display mode and editable mode with inline editing capabilities.
 * Can display expandable child rows and handle selection state.
 *
 * @example
 * ```tsx
 * // Regular mode
 * <TableRow
 *   row={rowData}
 *   columns={columns}
 *   onClickRow={(e, row) => console.log('Row clicked', row)}
 *   isRowSelected={(row) => row.id === selectedId}
 * />
 * ```
 *
 * @example
 * ```tsx
 * // Editable mode
 * <TableRow
 *   row={rowData}
 *   columns={columns}
 *   editable={{
 *     onAddRow: async (row) => { await api.create(row); return true; },
 *     onUpdateRow: async (row) => { await api.update(row); return true; },
 *     onDeleteRow: async (row) => { await api.delete(row); return true; },
 *     onCancelNewRow: () => console.log('Cancelled'),
 *     onCancelEditing: () => console.log('Edit cancelled')
 *   }}
 * />
 * ```
 */
const TableRowBase = (props) => {
    const { row: propRow, columns, onClickRow, renderCell, isRowSelected, onSelectRow, renderChildRows, editable, sortable = false, dragId, onSwipeLeft, onLongPress, selectMode, touchActions } = props;
    const [expanded, setExpanded] = useState(false);
    const [loading, setLoading] = useState(false);
    const [childRows, setChildRows] = useState([]);
    const [isEditing, setIsEditing] = useState(false);
    const [row, setRow] = useState(propRow);
    const [saving, setSaving] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
        id: dragId || '',
        disabled: !sortable,
    });
    useEffect(() => {
        if (expanded)
            loadChildren();
    }, [expanded]);
    useEffect(() => {
        setRow(propRow);
    }, [propRow]);
    const loadChildren = useCallback(() => __awaiter(void 0, void 0, void 0, function* () {
        if (!!(row === null || row === void 0 ? void 0 : row.children)) {
            setLoading(true);
            if (Array.isArray(row.children))
                setChildRows(row.children || []);
            else {
                const items = yield row.children(row);
                setChildRows(items);
            }
            setLoading(false);
        }
    }), [row]);
    const cancelEdit = useCallback(() => {
        var _a;
        if (row.__newitem__) {
            (_a = editable === null || editable === void 0 ? void 0 : editable.onCancelNewRow) === null || _a === void 0 ? void 0 : _a.call(editable);
        }
        setIsEditing(false);
        setRow(propRow);
    }, [editable === null || editable === void 0 ? void 0 : editable.onCancelNewRow, propRow, row.__newitem__]);
    const handleKeyboard = useCallback((row, e) => {
        var _a, _b;
        if (!editable)
            return;
        if (e.key === 'Escape') {
            e.stopPropagation();
            cancelEdit();
        }
        if (e.key === 'Enter') {
            e.stopPropagation();
            if (row.__newitem__) {
                (_a = editable === null || editable === void 0 ? void 0 : editable.onAddRow) === null || _a === void 0 ? void 0 : _a.call(editable, row).then((b) => {
                    if (b)
                        setIsEditing(false);
                }).catch((err) => {
                    console.error('Error adding row:', err);
                });
            }
            else {
                (_b = editable === null || editable === void 0 ? void 0 : editable.onUpdateRow) === null || _b === void 0 ? void 0 : _b.call(editable, row).then((b) => {
                    if (b)
                        setIsEditing(false);
                }).catch((err) => {
                    console.error('Error updating row:', err);
                });
            }
        }
    }, [cancelEdit, editable]);
    const renderSelectColumn = useCallback((item) => {
        return _jsx(Checkbox, { checked: (isRowSelected === null || isRowSelected === void 0 ? void 0 : isRowSelected(item)) || false, onChange: () => {
                if (onSelectRow)
                    onSelectRow(item);
            }, type: 'bordered', className: 'uxpcore_table__select-checkbox' });
    }, [isRowSelected, onSelectRow]);
    const renderExpandColumn = useCallback((item) => {
        if (!!(item === null || item === void 0 ? void 0 : item.children) && (Array.isArray(item.children) || typeof item.children == 'function')) {
            return _jsx("div", { className: "uxpcore_table__expand-btn", onClick: (e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setExpanded(prev => !prev);
                }, children: _jsx(FontAwesomeIconWrapper, { icon: ['fal', expanded ? 'chevron-up' : 'chevron-down'] }) });
        }
        return null;
    }, [expanded]);
    const renderDragHandleColumn = useCallback(() => {
        if (!sortable)
            return null;
        return (_jsx("div", Object.assign({ className: "uxpcore_table__drag-handle" }, listeners, attributes, { children: _jsx(FontAwesomeIconWrapper, { icon: ['fal', 'grip-lines'] }) })));
    }, [sortable, listeners, attributes]);
    function renderChildrenContent() {
        if ((childRows === null || childRows === void 0 ? void 0 : childRows.length) > 0) {
            if (!!renderChildRows)
                return renderChildRows(childRows || [], { columns, onClickRow, isRowSelected, onSelectRow, renderCell, renderChildRows });
            else
                return (_jsx("div", { className: "uxpcore_table__row_child", children: (childRows || []).map((cr, i) => _jsx(TableRow, { row: cr, columns: columns, onClickRow: onClickRow, isRowSelected: isRowSelected, onSelectRow: onSelectRow, renderChildRows: renderChildRows }, i)) }));
        }
        return _jsx("div", { className: "uxpcore_table__row_children--no-items", children: "No items found" });
    }
    const gestures = useRowGestures({
        onSwipeLeft: onSwipeLeft && !isEditing ? () => onSwipeLeft(row) : undefined,
        onLongPress: onLongPress && !isEditing ? () => onLongPress(row) : undefined,
    });
    const handleRowClick = (e) => {
        // The click after a swipe or long press is part of that gesture
        if (gestures.consumeClick())
            return;
        if (selectMode) {
            onSelectRow === null || onSelectRow === void 0 ? void 0 : onSelectRow(row);
        }
        else if (editable) {
            setIsEditing(true);
        }
        else {
            onClickRow === null || onClickRow === void 0 ? void 0 : onClickRow(e, row);
        }
    };
    const style = sortable ? {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
    } : {};
    return _jsxs("div", { ref: sortable ? setNodeRef : undefined, style: style, className: classNames('uxpcore_table__row', {
            'uxpcore_table__row--selected': (isRowSelected === null || isRowSelected === void 0 ? void 0 : isRowSelected(row)) || false,
            'uxpcore_table__row--expanded': expanded,
            'uxpcore_table__row--dragging': isDragging,
        }), children: [_jsxs("div", Object.assign({ className: classNames("uxpcore_table__row_content", { "uxpcore_table__row_content--touchactions": !!touchActions }), onClick: handleRowClick }, gestures.handlers, { onKeyDown: editable ? handleKeyboard.bind(null, row) : undefined, tabIndex: editable ? 0 : undefined, children: [touchActions && _jsx("div", { className: "uxpcore_table__row_touchactions", onClick: (e) => e.stopPropagation(), children: touchActions(row) }), Object.values(columns).map((column) => {
                        const renderColumn = (row, column) => {
                            var _a, _b, _c, _d, _e, _f;
                            if (column.id === '__dragHandleColumn__')
                                return renderDragHandleColumn();
                            if (column.id === '__selectColumn__')
                                return renderSelectColumn(row);
                            if (column.id === '__expandColumn__')
                                return renderExpandColumn(row);
                            // Handle editable actions column
                            if (column.id === '__editActionsColumn__' && editable) {
                                if (!isEditing && !row.__newitem__)
                                    return _jsx("div", {});
                                return _jsxs("div", { className: "uxpcore_table__cell__actions", children: [_jsx(ButtonComponent, { leftIcon: 'fas check', title: ((_a = editable === null || editable === void 0 ? void 0 : editable.labels) === null || _a === void 0 ? void 0 : _a.save) || 'Save', className: 'uxpcore_table__cell__actions_item uxpcore_table__cell__actions_item-save', onClick: e => {
                                                var _a, _b;
                                                e.stopPropagation();
                                                if (row.__newitem__) {
                                                    setSaving(true);
                                                    (_a = editable === null || editable === void 0 ? void 0 : editable.onAddRow) === null || _a === void 0 ? void 0 : _a.call(editable, row).then((b) => {
                                                        if (b)
                                                            setIsEditing(false);
                                                    }).catch((err) => {
                                                        console.error('Error adding row:', err);
                                                    }).finally(() => {
                                                        setSaving(false);
                                                    });
                                                }
                                                else {
                                                    setSaving(true);
                                                    (_b = editable === null || editable === void 0 ? void 0 : editable.onUpdateRow) === null || _b === void 0 ? void 0 : _b.call(editable, row).then((b) => {
                                                        if (b)
                                                            setIsEditing(false);
                                                    }).catch((err) => {
                                                        console.error('Error updating row:', err);
                                                    }).finally(() => {
                                                        setSaving(false);
                                                    });
                                                }
                                            }, loading: saving, active: !saving && !deleting }), _jsx(ButtonComponent, { title: ((_b = editable === null || editable === void 0 ? void 0 : editable.labels) === null || _b === void 0 ? void 0 : _b.cancel) || 'Cancel', className: 'uxpcore_table__cell__actions_item uxpcore_table__cell__actions_item-cancel', onClick: e => {
                                                e.stopPropagation();
                                                cancelEdit();
                                            }, active: !saving && !deleting }), _jsx(ButtonComponent, { leftIcon: 'fas times', title: ((_c = editable === null || editable === void 0 ? void 0 : editable.labels) === null || _c === void 0 ? void 0 : _c.delete) || 'Delete', className: 'uxpcore_table__cell__actions_item uxpcore_table__cell__actions_item-delete', onClick: e => {
                                                var _a;
                                                e.stopPropagation();
                                                if (row.__newitem__) {
                                                    console.warn('Delete action should not be called for new items');
                                                }
                                                else {
                                                    setDeleting(true);
                                                    (_a = editable === null || editable === void 0 ? void 0 : editable.onDeleteRow) === null || _a === void 0 ? void 0 : _a.call(editable, row).then((b) => {
                                                        if (b)
                                                            setIsEditing(false);
                                                    }).catch((err) => {
                                                        console.error('Error deleting row:', err);
                                                    }).finally(() => {
                                                        setDeleting(false);
                                                    });
                                                }
                                            }, loading: deleting, active: !saving && !deleting })] });
                            }
                            // Handle editable cell rendering
                            if (editable && row.__newitem__) {
                                if (column === null || column === void 0 ? void 0 : column.renderNewItem) {
                                    return (_d = column === null || column === void 0 ? void 0 : column.renderNewItem) === null || _d === void 0 ? void 0 : _d.call(column, row, (v) => {
                                        setRow(v);
                                    });
                                }
                                return _jsx(Input, { value: row[column.id], onChange: (v) => {
                                        setRow(Object.assign(Object.assign({}, row), { [column.id]: v }));
                                    }, fullWidth: true });
                            }
                            if (editable && isEditing) {
                                if (column === null || column === void 0 ? void 0 : column.renderEditItem) {
                                    return (_e = column === null || column === void 0 ? void 0 : column.renderEditItem) === null || _e === void 0 ? void 0 : _e.call(column, row, (v) => {
                                        setRow(v);
                                    });
                                }
                                return _jsx(Input, { value: row[column.id], onChange: (v) => {
                                        setRow(Object.assign(Object.assign({}, row), { [column.id]: v }));
                                    }, fullWidth: true });
                            }
                            // Normal rendering
                            if (!!renderCell) {
                                const result = renderCell(row, column);
                                if (result !== undefined)
                                    return result;
                            }
                            if (!!(column === null || column === void 0 ? void 0 : column.renderColumn))
                                return (_f = column === null || column === void 0 ? void 0 : column.renderColumn) === null || _f === void 0 ? void 0 : _f.call(column, row);
                            return row === null || row === void 0 ? void 0 : row[column === null || column === void 0 ? void 0 : column.id];
                        };
                        return _jsx(TableCell, { row: row, column: column, renderCell: renderColumn }, column.id);
                    })] })), expanded
                ? _jsx("div", { className: "uxpcore_table__row_children", children: loading
                        ? _jsx(LoadingSpinner, {})
                        : renderChildrenContent() })
                : null] });
};
const TableRow = React.memo(TableRowBase);
export { TableRow, SkeletonTableRow };
